import { useSearchParams } from 'react-router-dom';
import { SearchParam } from '@common/ts';
import { useState } from 'react';
import { ConversationNavigationType, TranscriptsView } from '@/enums';
import { AllTranscriptsProps } from '@/types';
import { useCurrentConversations } from './useCurrentConversations';
import { useMutateGetConversationsPageWithStore } from '@/reactQuery/get';
import { getConversationId } from '@/utils/getTranscriptId';

const ITEMS_PER_PAGE = 20;

const selectConversationFromPage = (items: AllTranscriptsProps[], isNext: boolean) => {
  return isNext ? items[0] : items[items.length - 1];
};

const getPageBoundaryInfo = (currentIndex: number, itemsPerPage: number) => ({
  isAtPageStart: currentIndex > 0 && currentIndex % itemsPerPage === 0,
  isAtPageEnd: (currentIndex + 1) % itemsPerPage === 0,
});

interface UseTranscriptNavigationProps {
  projectId: string;
  activeView?: TranscriptsView;
  urlCurrentPage: number;
  tableType: string;
  onScrollTo: (conversationId: string) => void;
}

export const useTranscriptNavigation = ({
  projectId,
  activeView,
  urlCurrentPage,
  tableType,
  onScrollTo,
}: UseTranscriptNavigationProps) => {
  const isCompletedTranscriptsView = activeView === TranscriptsView.COMPLETED;
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationId = searchParams.get(SearchParam.SELECTED_TRANSCRIPT) || '';
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const [direction, setDirection] = useState<ConversationNavigationType | null>(null);

  const {
    completedConversations,
    incompleteConversations,
    completedTotalPages,
    incompleteTotalPages,
    utils: { getPrevTranscript, getNextTranscript },
  } = useCurrentConversations();

  const { mutateAsync: mutateGetConversationsPage, isPending: isGetConversationsPagePending } =
    useMutateGetConversationsPageWithStore(isCompletedTranscriptsView, urlCurrentPage);
  const currentConversations = isCompletedTranscriptsView
    ? completedConversations
    : incompleteConversations;
  const currentTotalPages = isCompletedTranscriptsView ? completedTotalPages : incompleteTotalPages;

  // Get navigation transcripts from store
  const prevTranscript = getPrevTranscript(conversationId, currentConversations);
  const nextTranscript = getNextTranscript(conversationId, currentConversations);

  // Helper function for URL parameter management
  const updatePageInUrl = (page: number) => {
    if (page > 1) {
      searchParams.set(`${tableType}_page`, page.toString());
    } else {
      searchParams.delete(`${tableType}_page`);
    }
  };

  const removeSelectedTranscriptAndGoBack = () => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.delete(SearchParam.SELECTED_TRANSCRIPT);
    newSearchParams.delete(SearchParam.CONVERSATION_FROM_QUESTIONS_TAB);
    setSearchParams(newSearchParams);
    onScrollTo(conversationId);
  };

  const handleTranscriptNavigation = async (navigationDirection: ConversationNavigationType) => {
    const isNext = navigationDirection === ConversationNavigationType.NEXT;
    setDirection(navigationDirection);
    const targetTranscript = isNext ? nextTranscript : prevTranscript;
    if (!targetTranscript) {
      // Fetch next page using mutation
      const nextPage = isNext ? urlCurrentPage + 1 : urlCurrentPage - 1;

      try {
        const result = await mutateGetConversationsPage({
          projectId: projectId!,
          filterId,
          language: searchParams.get(SearchParam.TRANSLATION_LANGUAGE),
          conversationFilter: activeView,
          page: nextPage,
        });

        // Set the first conversation from the new page as selected
        if (result.items.length > 0) {
          const firstConversation = selectConversationFromPage(result.items, isNext);
          const transcriptId = getConversationId(firstConversation);
          if (transcriptId) {
            searchParams.set(SearchParam.SELECTED_TRANSCRIPT, transcriptId);
            updatePageInUrl(nextPage);
            setSearchParams(searchParams);
          }
        }
      } catch (error) {
        console.error('Failed to fetch next page:', error);
      }
      return;
    }

    // Check for page boundary crossings when there is a target transcript
    const currentIndex = currentConversations.findIndex(
      (conv) => getConversationId(conv) === conversationId,
    );

    if (!isNext) {
      // Check if we're going back and crossing page boundaries
      // Check if current conversation is at the start of a page (21st, 41st, 61st, etc.)
      // These would be at indices 20, 40, 60, etc. (0-based indexing)
      const { isAtPageStart } = getPageBoundaryInfo(currentIndex, ITEMS_PER_PAGE);

      if (isAtPageStart) {
        const prevPage = urlCurrentPage - 1;

        if (prevPage > 1) {
          searchParams.set(`${tableType}_page`, prevPage.toString());
        } else {
          searchParams.delete(`${tableType}_page`);
        }
      }
    } else {
      // Check if we're going forward and crossing page boundaries
      // Check if current conversation is at the end of a page (20th, 40th, 60th, etc.)
      // These would be at indices 19, 39, 59, etc. (0-based indexing)
      const { isAtPageEnd } = getPageBoundaryInfo(currentIndex, ITEMS_PER_PAGE);

      if (isAtPageEnd) {
        const nextPage = urlCurrentPage + 1;
        searchParams.set(`${tableType}_page`, nextPage.toString());
      }
    }

    const transcriptId = getConversationId(targetTranscript);
    if (!transcriptId) return;

    searchParams.set(SearchParam.SELECTED_TRANSCRIPT, transcriptId);
    setSearchParams(searchParams);
  };

  const handleNextTranscript = () => handleTranscriptNavigation(ConversationNavigationType.NEXT);
  const handlePrevTranscript = () => handleTranscriptNavigation(ConversationNavigationType.PREV);

  const isFirstConversation =
    getConversationId(currentConversations?.[0]) === conversationId && urlCurrentPage === 1;
  const isLastConversation =
    getConversationId(currentConversations?.[currentConversations.length - 1]) === conversationId &&
    urlCurrentPage === currentTotalPages;

  return {
    conversationId,
    prevTranscript,
    nextTranscript,
    isFirstConversation,
    isGetConversationsPagePending,
    removeSelectedTranscriptAndGoBack,
    handleNextTranscript,
    handlePrevTranscript,
    isLastConversation,
    direction,
  };
};
